import React from "react"
import { Link } from "@tanstack/react-router"
import {
  useAdminOrganizations,
  useCreateOrganization,
  useAdminSpeciaAdmins,
  useCreateSpeciaAdmin,
  useUpdateSpeciaAdmin,
  useDeleteSpeciaAdmin,
  useSendSpeciaAdminPasswordReset,
  useCurrentAdmin,
} from "@/lib/queries"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { MoreHorizontal, Plus, Shield } from "lucide-react"
import { toast } from "sonner"
import { hasPermission, PERMISSIONS } from "@/lib/permissions"
import type { AdminSpeciaAdmin } from "@/lib/types"

const PERMISSION_OPTIONS = [
  { key: "manage_users", label: "Manage Users & Organizations" },
  { key: "edit_docs", label: "Edit Documents" },
  { key: "publish_docs", label: "Publish Documents" },
]

export function UsersPage() {
  const { data: currentAdmin } = useCurrentAdmin()
  const canManageUsers = hasPermission(currentAdmin, PERMISSIONS.MANAGE_USERS)
  const canManageAdmins = currentAdmin?.role === "super_admin"

  const defaultTab = canManageUsers ? "organizations" : "admins"

  const { data: organizations = [], isLoading: orgsLoading } = useAdminOrganizations()
  const topLevelOrgs = organizations.filter((o) => !o.parent_organization_id)
  const createOrganization = useCreateOrganization()
  const { data: admins = [], isLoading: adminsLoading } = useAdminSpeciaAdmins()
  const createSpeciaAdmin = useCreateSpeciaAdmin()
  const updateSpeciaAdmin = useUpdateSpeciaAdmin()
  const deleteSpeciaAdmin = useDeleteSpeciaAdmin()
  const sendAdminPasswordReset = useSendSpeciaAdminPasswordReset()

  const [orgDialogOpen, setOrgDialogOpen] = React.useState(false)
  const [newOrgName, setNewOrgName] = React.useState("")

  const [adminDialogOpen, setAdminDialogOpen] = React.useState(false)
  const [newAdminEmail, setNewAdminEmail] = React.useState("")
  const [newAdminRole, setNewAdminRole] = React.useState("admin")
  const [newAdminPermissions, setNewAdminPermissions] = React.useState<string[]>([])

  const handleCreateOrganization = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newOrgName.trim()) return

    try {
      await createOrganization.mutateAsync({ name: newOrgName.trim() })
      toast.success("Organization created successfully")
      setOrgDialogOpen(false)
      setNewOrgName("")
    } catch (error) {
      toast.error("Failed to create organization")
    }
  }

  const handleCreateAdmin = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newAdminEmail.trim()) return

    try {
      await createSpeciaAdmin.mutateAsync({
        email: newAdminEmail.trim(),
        role: newAdminRole,
        permissions: newAdminRole === "super_admin" ? [] : newAdminPermissions,
      })
      toast.success("Admin created. A password reset email has been sent.")
      setAdminDialogOpen(false)
      setNewAdminEmail("")
      setNewAdminRole("admin")
      setNewAdminPermissions([])
    } catch (error) {
      toast.error("Failed to create admin")
    }
  }

  const handleToggleAdminActive = async (adminId: number, currentActive: boolean) => {
    try {
      await updateSpeciaAdmin.mutateAsync({
        id: adminId,
        active: !currentActive,
      })
      toast.success(`Admin ${!currentActive ? "activated" : "deactivated"}`)
    } catch (error) {
      toast.error("Failed to update admin")
    }
  }

  const handleSendAdminPasswordReset = async (adminId: number) => {
    try {
      await sendAdminPasswordReset.mutateAsync(adminId)
      toast.success("Password reset email sent")
    } catch (error) {
      toast.error("Failed to send password reset")
    }
  }

  const handleDeleteAdmin = async (adminId: number, email: string) => {
    if (!confirm(`Are you sure you want to delete admin "${email}"? This action cannot be undone.`)) {
      return
    }

    try {
      await deleteSpeciaAdmin.mutateAsync(adminId)
      toast.success("Admin deleted successfully")
    } catch (error) {
      toast.error("Failed to delete admin")
    }
  }

  const handleUpdatePermissions = async (admin: AdminSpeciaAdmin, permissions: string[]) => {
    try {
      await updateSpeciaAdmin.mutateAsync({
        id: admin.id,
        permissions,
      })
      toast.success("Permissions updated")
    } catch (error) {
      toast.error("Failed to update permissions")
    }
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "Never"
    return new Date(dateString).toLocaleDateString("en-CA")
  }

  return (
    <div className="space-y-6 overflow-auto p-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Users</h1>
        <p className="text-sm text-muted-foreground">
          Manage organizations and administrators
        </p>
      </div>

      <Tabs defaultValue={defaultTab} className="space-y-4">
        <TabsList>
          {canManageUsers && <TabsTrigger value="organizations">Organizations</TabsTrigger>}
          {canManageAdmins && <TabsTrigger value="admins">Admins</TabsTrigger>}
        </TabsList>

        {/* Organizations Tab */}
        {canManageUsers && (
        <TabsContent value="organizations" className="space-y-4">
          <div className="flex items-center justify-end">
            <Dialog open={orgDialogOpen} onOpenChange={setOrgDialogOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="mr-2 h-4 w-4" />
                  New Organization
                </Button>
              </DialogTrigger>
              <DialogContent>
                <form onSubmit={handleCreateOrganization}>
                  <DialogHeader>
                    <DialogTitle>Create Organization</DialogTitle>
                    <DialogDescription>
                      Add a new customer organization to the system
                    </DialogDescription>
                  </DialogHeader>
                  <div className="grid gap-4 py-4">
                    <div className="grid gap-2">
                      <Label htmlFor="orgName">Organization Name</Label>
                      <Input
                        id="orgName"
                        value={newOrgName}
                        onChange={(e) => setNewOrgName(e.target.value)}
                        placeholder="Enter organization name"
                        autoFocus
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setOrgDialogOpen(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={!newOrgName.trim() || createOrganization.isPending}
                    >
                      {createOrganization.isPending ? "Creating..." : "Create"}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {orgsLoading ? (
            <p className="text-muted-foreground">Loading organizations...</p>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Users</TableHead>
                    <TableHead>Created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {topLevelOrgs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-muted-foreground">
                        No organizations found
                      </TableCell>
                    </TableRow>
                  ) : (
                    topLevelOrgs.map((org) => (
                      <TableRow key={org.id} className="cursor-pointer">
                        <TableCell>
                          <Link
                            to="/admin/organizations/$id"
                            params={{ id: String(org.id) }}
                            className="font-medium hover:underline"
                          >
                            {org.name}
                          </Link>
                        </TableCell>
                        <TableCell className="text-xs">
                          <span className={org.active ? "text-lime-600 font-medium" : "text-muted-foreground"}>
                            {org.active ? "Active" : "Deactivated"}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">{org.user_count}</TableCell>
                        <TableCell className="text-muted-foreground">
                          {formatDate(org.created_at)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>
        )}

        {/* Admins Tab */}
        {canManageAdmins && (
        <TabsContent value="admins" className="space-y-4">
          <div className="flex items-center justify-end">
            <Dialog open={adminDialogOpen} onOpenChange={setAdminDialogOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="mr-2 h-4 w-4" />
                  New Admin
                </Button>
              </DialogTrigger>
              <DialogContent>
                <form onSubmit={handleCreateAdmin}>
                  <DialogHeader>
                    <DialogTitle>Create Admin</DialogTitle>
                    <DialogDescription>
                      Add a new administrator. A password reset email will be sent.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="grid gap-4 py-4">
                    <div className="grid gap-2">
                      <Label htmlFor="adminEmail">Email</Label>
                      <Input
                        id="adminEmail"
                        type="email"
                        value={newAdminEmail}
                        onChange={(e) => setNewAdminEmail(e.target.value)}
                        placeholder="Enter email address"
                        autoFocus
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="adminRole">Role</Label>
                      <Select value={newAdminRole} onValueChange={setNewAdminRole}>
                        <SelectTrigger id="adminRole">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="super_admin">Super Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {newAdminRole !== "super_admin" ? (
                      <div className="space-y-2">
                        <Label>Permissions</Label>
                        {PERMISSION_OPTIONS.map((perm) => (
                          <div key={perm.key} className="flex items-center gap-2">
                            <Checkbox
                              id={`new-${perm.key}`}
                              checked={newAdminPermissions.includes(perm.key)}
                              onCheckedChange={(checked) => {
                                setNewAdminPermissions((prev) =>
                                  checked
                                    ? [...prev, perm.key]
                                    : prev.filter((p) => p !== perm.key)
                                )
                              }}
                            />
                            <Label htmlFor={`new-${perm.key}`} className="font-normal">
                              {perm.label}
                            </Label>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">Super admins have all permissions.</p>
                    )}
                  </div>
                  <DialogFooter>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setAdminDialogOpen(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={!newAdminEmail.trim() || createSpeciaAdmin.isPending}
                    >
                      {createSpeciaAdmin.isPending ? "Creating..." : "Create"}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {adminsLoading ? (
            <p className="text-muted-foreground">Loading admins...</p>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Permissions</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Last Login</TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {admins.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-muted-foreground">
                        No admins found
                      </TableCell>
                    </TableRow>
                  ) : (
                    admins.map((admin) => (
                      <TableRow key={admin.id}>
                        <TableCell className="font-medium">
                          {admin.email}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {admin.role === "super_admin" ? "Super Admin" : "Admin"}
                        </TableCell>
                        <TableCell>
                          {admin.role === "super_admin" ? (
                            <span className="text-xs text-muted-foreground">All</span>
                          ) : (
                            <div className="flex flex-wrap gap-1">
                              {(admin.permissions ?? []).length === 0 ? (
                                <span className="text-xs text-muted-foreground">None</span>
                              ) : (
                                admin.permissions.map((p) => (
                                  <span key={p} className="text-xs text-muted-foreground">
                                    {PERMISSION_OPTIONS.find((o) => o.key === p)?.label ?? p}
                                  </span>
                                ))
                              )}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-xs">
                          <span className={admin.active ? (admin.sign_in_count === 0 ? "text-muted-foreground" : "text-lime-600 font-medium") : "text-muted-foreground"}>
                            {admin.active ? (admin.sign_in_count === 0 ? "Invited" : "Active") : "Deactivated"}
                          </span>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {formatDate(admin.last_sign_in_at)}
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() =>
                                  handleToggleAdminActive(admin.id, admin.active)
                                }
                              >
                                {admin.active ? "Deactivate" : "Activate"}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleSendAdminPasswordReset(admin.id)}
                              >
                                Send Password Reset
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() =>
                                  handleDeleteAdmin(admin.id, admin.email)
                                }
                                className="text-destructive"
                              >
                                Delete Admin
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                          {admin.role !== "super_admin" && (
                            <PermissionsPopover
                              admin={admin}
                              onSave={(permissions) => handleUpdatePermissions(admin, permissions)}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>
        )}
      </Tabs>
    </div>
  )
}

function PermissionsPopover({
  admin,
  onSave,
}: {
  admin: AdminSpeciaAdmin
  onSave: (permissions: string[]) => void
}) {
  const [selected, setSelected] = React.useState<string[]>(admin.permissions ?? [])
  const [open, setOpen] = React.useState(false)

  // Sync when popover opens
  React.useEffect(() => {
    if (open) {
      setSelected(admin.permissions ?? [])
    }
  }, [open, admin.permissions])

  const handleSave = () => {
    onSave(selected)
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" title="Edit Permissions">
          <Shield className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64" align="end">
        <div className="space-y-3">
          <p className="text-sm font-medium">Permissions</p>
          {PERMISSION_OPTIONS.map((perm) => (
            <div key={perm.key} className="flex items-center gap-2">
              <Checkbox
                id={`edit-${admin.id}-${perm.key}`}
                checked={selected.includes(perm.key)}
                onCheckedChange={(checked) => {
                  setSelected((prev) =>
                    checked
                      ? [...prev, perm.key]
                      : prev.filter((p) => p !== perm.key)
                  )
                }}
              />
              <Label htmlFor={`edit-${admin.id}-${perm.key}`} className="font-normal text-sm">
                {perm.label}
              </Label>
            </div>
          ))}
          <Button size="sm" onClick={handleSave} className="w-full">
            Save
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
